import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { ScrollText, Coins, DollarSign, ShieldAlert, Globe, Scale, MessageSquareText } from "lucide-react";
import { SMS_CONSENT_EN } from "@/pages/Privacy";

const Section = ({ icon: Icon, title, accent, children }) => (
  <section className="glass rounded-2xl p-6 border border-white/10">
    <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
      <Icon className={`w-5 h-5 ${accent}`} /> {title}
    </h2>
    <div className="space-y-2 text-sm text-gray-300 leading-relaxed">{children}</div>
  </section>
);

export default function Terms() {
  useSeo({ title: "Terms & Rules", path: "/terms", description: "PhonePlay terms of use and contest rules: a free social game played with Fun Coins that have no monetary value. 18+ only." });
  return (
    <div data-testid="terms-page" className="p-4 lg:p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <ScrollText className="w-8 h-8 text-amber-400" />
        <h1 className="font-display text-3xl sm:text-4xl font-black">Terms &amp; Rules</h1>
      </div>
      <p className="text-gray-400 mb-8 text-sm">
        Please read these terms, contest rules and responsible-gaming disclosures before playing. Last updated June 2026.
      </p>

      <div className="space-y-5">
        <Section icon={Scale} title="1. A Free Social Game" accent="text-purple-400">
          <p>PhonePlay is a free-to-play social gaming platform. Everything on PhonePlay is played with <span className="text-amber-300 font-semibold">Fun Coins</span>:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-400">
            <li><span className="text-amber-300 font-semibold">PhonePlay Arcade</span> — arcade-style chance games played with Fun Coins for entertainment only.</li>
            <li><span className="text-emerald-300 font-semibold">Skill Games</span> — 1v1 duels and tournaments against real players, entered with Fun Coins and scored on our servers.</li>
          </ul>
          <p className="text-gray-400">There is no real-money gambling on PhonePlay. No game, contest or prize on PhonePlay has monetary value.</p>
        </Section>

        <Section icon={Coins} title="2. Fun Coins Are Not Money" accent="text-amber-400">
          <p>Fun coins have <span className="font-semibold text-amber-300">no monetary value</span>. They cannot be
          exchanged for money, redeemed, transferred, sold, or exchanged for money or prizes of any kind.</p>
          <p>All chance-based games — Slots, Wheel, Plinko, Crash, Mines, Crash Mines, Wheel Slots, Blackjack, Roulette
          and Baccarat — are played with fun coins only and can <span className="font-semibold">never</span> award money.
          Coins may be purchased for entertainment; a purchase is final and grants coins only.</p>
        </Section>

        <Section icon={DollarSign} title="3. Skill Games — Rules" accent="text-emerald-400">
          <p>Skill Tournaments and Head-to-Head Duels are games of skill. Outcomes are determined by player performance
          (speed, accuracy, reflex, memory and vocabulary), scored on our servers.</p>
          <p className="font-semibold text-gray-200">Entry &amp; prizes (Fun Coins only):</p>
          <ul className="list-disc list-inside space-y-1 text-gray-400">
            <li>Players enter a contest with a Fun Coin entry (the amount is shown before you join).</li>
            <li>A platform fee of <span className="font-mono text-emerald-300">10%</span> of the entries is retained; the remaining Fun Coins form the prize pool.</li>
            <li>Tournaments pay the top 3 finishers (50% / 30% / 20% of the pool). Duels pay the winner the combined entries minus the platform fee.</li>
            <li>Draws are refunded in Fun Coins. Unaccepted duel challenges can be cancelled for a full Fun Coin refund.</li>
            <li>Duels are played against real opponents only — never bots. If no opponent is available your entry is held and fully refunded if you cancel.</li>
            <li>All entries, prize pools and prizes are Fun Coins. They have no monetary value and cannot be exchanged for money or redeemed.</li>
          </ul>
        </Section>

        <Section icon={Globe} title="4. Eligibility &amp; Coin Purchases" accent="text-cyan-400">
          <p>You must be <span className="font-semibold">18 or older</span> to use PhonePlay. One account per person.
          Providing false age or identity information is prohibited and may result in account closure.</p>
          <p>Optional Fun Coin packs may be purchased for entertainment. Purchases are final, grant Fun Coins only, and
          never create a money balance, payout or redemption right.</p>
        </Section>

        <Section icon={ShieldAlert} title="5. Responsible Play" accent="text-emerald-400">
          <p>Play should be fun. You can set daily coin-purchase limits, activate a cooldown, or self-exclude at any time
          from the Responsible Gaming page. During self-exclusion you cannot enter skill games.</p>
          <p>If gaming stops being fun, please take a break and seek support.</p>
        </Section>

        <Section icon={Scale} title="6. Fair Play &amp; Account" accent="text-purple-400">
          <p>Automated play, collusion, multiple accounts, and exploiting bugs are prohibited. We use append-only ledgers and
          audit logs. We may suspend accounts and reverse transactions obtained through fraud or abuse.</p>
          <p>Chance games use provably-fair random generation; a seed is provided so you can verify results.</p>
        </Section>

        <Section icon={MessageSquareText} title="7. SMS Communications" accent="text-cyan-400">
          <p>If you choose to add a mobile number, PhonePlay sends transactional texts only: one-time passcodes for phone
          verification and two-factor login, plus account activity alerts. No marketing texts.</p>
          <p data-testid="terms-sms-consent" className="rounded-xl bg-cyan-500/10 border border-cyan-500/25 px-4 py-3 text-cyan-100 text-xs">{SMS_CONSENT_EN}</p>
          <p>Reply <span className="font-mono">STOP</span> to opt out at any time or remove your number in Wallet. Mobile
          information is never shared with third parties for marketing. Full details in our <Link to="/privacy" className="text-cyan-300 hover:underline">Privacy Policy</Link>.</p>
        </Section>

        <p className="text-xs text-gray-600 pt-2">
          These terms are provided for transparency and may be updated. 18+ · Coins have no monetary value · No real-money gambling.
        </p>
      </div>
    </div>
  );
}
